import {
  faro,
  Faro,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

/**
 * Initialize Grafana Faro monitoring.
 * This should be called as early as possible in the application lifecycle.
 *
 * Based on: https://github.com/grafana/faro-web-sdk/blob/main/docs/sources/tutorials/use-angular.md
 */
export function initializeFaroMonitoring(config: {
  collectorUrl: string;
  appName: string;
  appVersion: string;
  environment: string;
}): Faro {
  return initializeFaro({
    url: config.collectorUrl,
    app: {
      name: config.appName,
      version: config.appVersion,
      environment: config.environment,
    },
    // Ignore Faro collector URLs to prevent infinite loop of tracing its own requests
    ignoreUrls: [/faro-collect/, /grafana\.net\/collect/],
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: true,
      }),
      new TracingInstrumentation(),
    ],
    sessionTracking: {
      enabled: true,
      persistent: true,
    },
    batching: {
      enabled: true,
      sendTimeout: 1000,
      itemLimit: 50,
    },
  });
}

/**
 * Get the global Faro instance.
 * Use this to access Faro API for logging, events, measurements, etc.
 */
export function getFaro(): Faro {
  return faro;
}

/**
 * Helper to push a custom event
 */
export function pushEvent(
  name: string,
  attributes?: Record<string, string>,
): void {
  faro.api.pushEvent(name, attributes);
}

/**
 * Helper to push an error
 */
export function pushError(
  error: Error,
  context?: Record<string, string>,
): void {
  if (faro?.api) {
    faro.api.pushError(error, { context });
  }
}

/**
 * Helper to push a log message
 */
export function pushLog(
  message: string,
  context?: Record<string, string>,
): void {
  faro.api.pushLog([message], { context });
}

/**
 * Helper to push a measurement
 */
export function pushMeasurement(
  type: string,
  values: Record<string, number>,
  context?: Record<string, string>,
): void {
  faro.api.pushMeasurement({ type, values }, { context });
}

/**
 * Helper to set user information
 */
export function setUser(
  userId: string,
  attributes?: Record<string, string>,
): void {
  faro.api.setUser({ id: userId, ...attributes });
}

/**
 * Helper to clear user information
 */
export function resetUser(): void {
  faro.api.resetUser();
}
